import React, { useState } from 'react';
import { api } from '../utils/api';
import { toast } from 'sonner';

/**
 * RepriceBookingsDialog — admin tool to bulk-fix booking prices that
 * were created under stale/wrong pricing rules.
 *
 * Workflow:
 *   1. Pick a strategy:
 *        • Flat base   → price = template.base_price
 *        • Recompute   → re-run the live calculate_price() using the
 *                        template's current tier config
 *   2. Click 👀 Preview — server returns a dry-run summary (no DB
 *      mutation). Shows totals, by-template breakdown, sample rows.
 *   3. Click ✅ Commit — server applies the change, snapshots the old
 *      price into ``previous_price_paid`` and audit-logs the batch so
 *      it can be undone with one click later.
 *
 * Safety rails (enforced server-side too):
 *   • Tenant-scoped.
 *   • By default only touches `status=confirmed` AND future-dated
 *     sessions — never repriced bookings for already-attended sessions.
 *   • Confirmation phrase ("REPRICE") required before commit.
 */
export function RepriceBookingsDialog({ open, onClose }) {
  const [strategy, setStrategy] = useState('flat_base');
  const [futureOnly, setFutureOnly] = useState(true);
  const [includeCancelled, setIncludeCancelled] = useState(false);
  const [includeCompleted, setIncludeCompleted] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [undoing, setUndoing] = useState(false);

  if (!open) return null;

  const statusIn = ['confirmed', ...(includeCancelled ? ['cancelled'] : []), ...(includeCompleted ? ['completed'] : [])];

  const runPreview = async () => {
    setLoading(true);
    setPreview(null);
    setConfirmText('');
    try {
      const r = await api.repriceBookings({
        strategy,
        dry_run: true,
        status_in: statusIn,
        future_only: futureOnly,
      });
      setPreview(r);
    } catch (e) {
      toast.error('Preview failed', { description: e.message });
    } finally {
      setLoading(false);
    }
  };

  const runCommit = async () => {
    if (confirmText.trim().toUpperCase() !== 'REPRICE') {
      toast.error('Type REPRICE to confirm.');
      return;
    }
    setCommitting(true);
    try {
      const r = await api.repriceBookings({
        strategy,
        dry_run: false,
        status_in: statusIn,
        future_only: futureOnly,
      });
      toast.success(`Repriced ${r.would_change} booking(s)`, {
        description: `Net change £${r.net_change.toFixed(2)} · batch ${r.batch_id.slice(0, 8)}…`,
      });
      onClose?.();
    } catch (e) {
      toast.error('Reprice failed', { description: e.message });
    } finally {
      setCommitting(false);
    }
  };

  const runUndo = async () => {
    if (!window.confirm('Undo the most recent reprice batch for this org? Affected bookings will revert to their pre-reprice price.')) return;
    setUndoing(true);
    try {
      const r = await api.undoLastReprice();
      toast.success(`Reverted ${r.reverted} booking(s)`, {
        description: `Undid batch ${r.undo_of_batch.slice(0, 8)}…`,
      });
      onClose?.();
    } catch (e) {
      toast.error('Undo failed', { description: e.message });
    } finally {
      setUndoing(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="reprice-bookings-dialog">
      <div className="modal-content" style={{ maxWidth: 720 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid hsl(var(--border))' }}>
          <h2 style={{ margin: 0 }}>💱 Reprice bookings</h2>
          <p style={{ margin: '0.25rem 0 0', fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))' }}>
            Bulk-correct booking prices for bookings created under stale tier rules. <strong>Past sessions are protected</strong> by default. Every change is audit-logged and reversible.
          </p>
        </div>

        <div style={{ padding: '1rem 1.25rem', display: 'flex', flexDirection: 'column', gap: '0.85rem', maxHeight: '70vh', overflowY: 'auto' }}>
          <div>
            <strong style={{ fontSize: '0.85rem' }}>Pricing strategy</strong>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '0.5rem 0.65rem', border: '1px solid hsl(var(--border))', borderRadius: 8, background: strategy === 'flat_base' ? 'hsl(var(--primary)/0.06)' : 'transparent', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="reprice-strategy"
                  checked={strategy === 'flat_base'}
                  onChange={() => { setStrategy('flat_base'); setPreview(null); }}
                  data-testid="reprice-strategy-flat"
                  style={{ marginTop: 3 }}
                />
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>📌 Flat base price</div>
                  <div style={{ fontSize: '0.78rem', color: 'hsl(var(--muted-foreground))' }}>
                    Set every matching booking to the template's <code>base_price</code>. Strips both early-bird and last-minute variation entirely.
                  </div>
                </div>
              </label>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '0.5rem 0.65rem', border: '1px solid hsl(var(--border))', borderRadius: 8, background: strategy === 'recompute_tiers' ? 'hsl(var(--primary)/0.06)' : 'transparent', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="reprice-strategy"
                  checked={strategy === 'recompute_tiers'}
                  onChange={() => { setStrategy('recompute_tiers'); setPreview(null); }}
                  data-testid="reprice-strategy-recompute"
                  style={{ marginTop: 3 }}
                />
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>🔄 Recompute with current tier config</div>
                  <div style={{ fontSize: '0.78rem', color: 'hsl(var(--muted-foreground))' }}>
                    Re-runs the live pricing engine using each template's <strong>current</strong> early-bird / last-minute settings. Useful after you've already adjusted the tier values.
                  </div>
                </div>
              </label>
            </div>
          </div>

          <div>
            <strong style={{ fontSize: '0.85rem' }}>Scope</strong>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6, fontSize: '0.82rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input
                  type="checkbox"
                  checked={futureOnly}
                  onChange={(e) => { setFutureOnly(e.target.checked); setPreview(null); }}
                  data-testid="reprice-future-only"
                />
                Future sessions only (recommended — protects historical pay)
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input
                  type="checkbox"
                  checked={includeCancelled}
                  onChange={(e) => { setIncludeCancelled(e.target.checked); setPreview(null); }}
                  data-testid="reprice-include-cancelled"
                />
                Include cancelled bookings (rare)
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input
                  type="checkbox"
                  checked={includeCompleted}
                  onChange={(e) => { setIncludeCompleted(e.target.checked); setPreview(null); }}
                  data-testid="reprice-include-completed"
                />
                Include completed bookings (caution — past attendance)
              </label>
            </div>
          </div>

          <div>
            <button
              type="button"
              className="btn btn-primary"
              onClick={runPreview}
              disabled={loading || committing}
              data-testid="reprice-preview-btn"
            >
              {loading ? 'Previewing…' : '👀 Preview impact'}
            </button>
          </div>

          {preview && (
            <div className="card" style={{ background: 'hsl(var(--muted)/0.4)', padding: '0.85rem' }} data-testid="reprice-preview-card">
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: '0.88rem' }}>
                <div>📊 <strong>Would change:</strong> {preview.would_change} of {preview.matched}</div>
                <div>💷 <strong>Before total:</strong> £{preview.total_before.toFixed(2)}</div>
                <div>💷 <strong>After total:</strong> £{preview.total_after.toFixed(2)}</div>
                <div style={{ color: preview.net_change < 0 ? 'hsl(140 60% 30%)' : preview.net_change > 0 ? 'hsl(35 80% 30%)' : 'inherit' }}>
                  {preview.net_change < 0 ? '🟢' : preview.net_change > 0 ? '🟡' : '⚪'} <strong>Net:</strong> £{preview.net_change.toFixed(2)}
                </div>
              </div>

              {preview.by_template && preview.by_template.length > 0 && (
                <details style={{ marginTop: '0.6rem', fontSize: '0.82rem' }}>
                  <summary><strong>Per template breakdown</strong></summary>
                  <table style={{ width: '100%', marginTop: 6, borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ textAlign: 'left', color: 'hsl(var(--muted-foreground))' }}>
                        <th style={{ padding: '0.2rem 0.4rem', borderBottom: '1px solid hsl(var(--border))' }}>Template</th>
                        <th style={{ padding: '0.2rem 0.4rem', borderBottom: '1px solid hsl(var(--border))', textAlign: 'right' }}>Count</th>
                        <th style={{ padding: '0.2rem 0.4rem', borderBottom: '1px solid hsl(var(--border))', textAlign: 'right' }}>Before</th>
                        <th style={{ padding: '0.2rem 0.4rem', borderBottom: '1px solid hsl(var(--border))', textAlign: 'right' }}>After</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.by_template.map((t) => (
                        <tr key={t.template_id}>
                          <td style={{ padding: '0.2rem 0.4rem' }}>{t.template_name || t.template_id}</td>
                          <td style={{ padding: '0.2rem 0.4rem', textAlign: 'right' }}>{t.count}</td>
                          <td style={{ padding: '0.2rem 0.4rem', textAlign: 'right' }}>£{t.before.toFixed(2)}</td>
                          <td style={{ padding: '0.2rem 0.4rem', textAlign: 'right' }}>£{t.after.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </details>
              )}

              {preview.sample && preview.sample.length > 0 && (
                <details style={{ marginTop: '0.4rem', fontSize: '0.78rem' }}>
                  <summary>Sample rows (up to 8)</summary>
                  <ul style={{ margin: '4px 0 0 1rem' }}>
                    {preview.sample.map((s, i) => (
                      <li key={i}>
                        {s.session_date?.slice(0, 10)} · {s.template_name} · £{s.old_price.toFixed(2)} → <strong>£{s.new_price.toFixed(2)}</strong>
                      </li>
                    ))}
                  </ul>
                </details>
              )}

              {preview.would_change > 0 && (
                <>
                  <div style={{ marginTop: '0.85rem', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <label style={{ fontSize: '0.82rem' }}>
                      Type <code style={{ background: 'hsl(var(--muted))', padding: '0 4px', borderRadius: 3 }}>REPRICE</code> to confirm:
                    </label>
                    <input
                      type="text"
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value)}
                      data-testid="reprice-confirm-text"
                      placeholder="REPRICE"
                      autoComplete="off"
                      style={{ fontSize: '0.85rem' }}
                    />
                  </div>
                  <div style={{ marginTop: '0.5rem' }}>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={runCommit}
                      disabled={committing || confirmText.trim().toUpperCase() !== 'REPRICE'}
                      data-testid="reprice-commit-btn"
                    >
                      {committing ? 'Working…' : `✅ Commit reprice (${preview.would_change} booking${preview.would_change === 1 ? '' : 's'})`}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          <div style={{ borderTop: '1px solid hsl(var(--border))', paddingTop: '0.85rem', fontSize: '0.78rem' }}>
            <strong>Need to revert?</strong> If you ran a reprice batch and want to roll back, this restores every affected booking to the price it held before the most recent batch.
            <div style={{ marginTop: 4 }}>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={runUndo}
                disabled={undoing}
                data-testid="reprice-undo-btn"
                style={{ fontSize: '0.78rem', padding: '0.2rem 0.5rem' }}
              >
                {undoing ? 'Reverting…' : '↩️ Undo last reprice'}
              </button>
            </div>
          </div>
        </div>

        <div style={{ padding: '0.85rem 1.25rem', borderTop: '1px solid hsl(var(--border))', display: 'flex', justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={committing} data-testid="reprice-close-btn">Close</button>
        </div>
      </div>
    </div>
  );
}

export default RepriceBookingsDialog;
